import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import DateRangePicker from '@/components/ui/DateRangePicker'; 
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import DetalhesVendaModal from '../components/DetalhesVendaModal'; 
import SubscriptionBlocked from '@/components/SubscriptionBlocked';
import { 
  TrendingUp, 
  DollarSign, 
  Percent, 
  ShoppingBag, 
  Gift, 
  PlaneTakeoff,
  Eye,
  FileText,
  Loader2 // Adicionado
} from 'lucide-react';

// Componente de Card de Estatística
const StatCard = ({ title, value, icon, description }) => (
  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-medium text-gray-600">{title}</h3>
      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600">
        {icon}
      </div>
    </div>
    <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
    {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
  </div>
);

// Funções de parse de data
const parseAniversarioDate = (dateString) => {
    if (!dateString || String(dateString).trim() === '') return null;
    try {
        let date;
        if (String(dateString).includes('T')) {
            date = new Date(dateString);
        } else if (String(dateString).match(/^\d{4}-\d{2}-\d{2}$/)) {
            date = new Date(dateString + 'T00:00:00Z'); 
        } else if (String(dateString).match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
             date = new Date(dateString.replace(' ', 'T') + 'Z'); 
        } else if (String(dateString).match(/^\d{2}\/\d{2}\/\d{4}/)) {
             const parts = dateString.split(' ')[0].split('/');
             date = new Date(Date.UTC(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0])));
        } else {
            const timestamp = Date.parse(dateString);
            if(!isNaN(timestamp)) date = new Date(timestamp);
            else throw new Error("Formato não reconhecido");
        }
        if (isNaN(date.getTime())) throw new Error("Data inválida após parse");
        return date;
    } catch (e) {
        console.warn(`Erro data: ${dateString}`, e.message);
        return null;
    }
};

const formatAniversario = (dateString) => {
    if (!dateString || String(dateString).trim() === '') return 'Invalid Date';
    try {
        const date = parseAniversarioDate(dateString);
        if (!date) return 'Invalid Date';
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
    } catch (e) {
        return 'Invalid Date';
    }
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: firstDay, to: lastDay };
};

export default function PaginaInicio() {
  // === ESTADOS DE ASSINATURA ===
  const [statusLoading, setStatusLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  // === ESTADOS DO DASHBOARD ===
  const [stats, setStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [filters, setFilters] = useState({ date: getCurrentMonthRange() });
  
  // === ESTADOS DO MODAL ===
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // 1. Verificar Status da Assinatura ao carregar
  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await axiosInstance.get('/assinatura/status');
      setSubscriptionStatus(res.data.status); 
      setSubscriptionData(res.data);
    } catch (error) {
      console.error("Erro ao verificar status", error);
      if (error.response?.status === 402) {
           setSubscriptionStatus("INADIMPLENTE");
      }
    } finally {
      setStatusLoading(false);
    }
  };

  // 2. Carregar Dados do Dashboard (apenas inicia se o status for verificado, mas o axios pode barrar)
  useEffect(() => {
    if (!statusLoading && subscriptionStatus !== 'INADIMPLENTE' && subscriptionStatus !== 'CANCELADA') {
        loadData();
    }
  }, [statusLoading, subscriptionStatus]); // Dependências para carregar na ordem certa

  const loadData = async () => {
    setDataLoading(true);
    const params = {};
    
    if (filters.date?.from) {
        const localDate = new Date(filters.date.from);
        params.data_inicio = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())).toISOString();
    }
    if (filters.date?.to) {
        const localDate = new Date(filters.date.to);
        params.data_fim = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 23, 59, 59)).toISOString();
    }
    
    try {
      const [kpiRes, widgetsRes] = await Promise.all([
        axiosInstance.get('/dashboard/kpis', { params }),
        axiosInstance.get('/dashboard/widgets', { params }),
      ]);
      
      setStats({ ...widgetsRes.data, ...kpiRes.data });
    } catch (error) {
      // Se for erro de bloqueio (402), não exibe toast de erro genérico, pois a tela de bloqueio aparecerá
      if (error.response?.status !== 402) {
          toast.error('Erro ao carregar dados do dashboard');
      }
    } finally {
      setDataLoading(false);
    }
  };

  const handleShowDetails = async (vendaId) => {
    if (!vendaId) return toast.error("ID da venda não encontrado.");
    setIsModalLoading(true);
    setIsModalOpen(true);
    setSelectedVenda(null); 
    try {
      const response = await axiosInstance.get(`/vendas/${vendaId}`);
      setSelectedVenda(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes.');
      setIsModalOpen(false);
    } finally {
      setIsModalLoading(false);
    }
  };

  // === RENDERIZAÇÃO CONDICIONAL ===

  // 1. Carregando Status Inicial
  if (statusLoading) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600"/></div>;
  }

  // 2. Bloqueio de Assinatura (Exibe Tela de Cobrança)
  if (subscriptionStatus === 'INADIMPLENTE' || subscriptionStatus === 'CANCELADA') {
      return <SubscriptionBlocked dados={subscriptionData} />;
  }

  // 3. Carregando Dados do Dashboard ou Sem Dados
  if (dataLoading || !stats) {
    return (
        <div className="container mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando dashboard...</p>
          </div>
        </div>
    );
  }

  // 4. Dashboard Completo
  const faturamento_bruto_total = stats.faturamento_bruto_total || 0;
  const repasses_fornecedores = stats.repasses_fornecedores || 0;
  const receita_liquida_agencia_total = stats.receita_liquida_agencia_total || 0;
  const margem_lucro = stats.margem_lucro || 0;
  const volume_vendas = stats.volume_vendas || 0;
  const ticket_medio = stats.ticket_medio || 0;

  const getProximoAniversario = (dataString) => {
    const dataNasc = parseAniversarioDate(dataString);
    if (!dataNasc) return Infinity;

    const hoje = new Date();
    
    // CORREÇÃO: Cria uma versão de "Hoje" zerada em UTC para comparar maçãs com maçãs
    // Isso evita que o aniversário de hoje (00:00 UTC) seja considerado menor que hoje (00:00 BRT = 03:00 UTC)
    const hojeUTC = new Date(Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));

    // Cria o aniversário neste ano em UTC
    const proximoNiver = new Date(Date.UTC(hoje.getFullYear(), dataNasc.getUTCMonth(), dataNasc.getUTCDate()));
    
    // Agora a comparação é justa: UTC contra UTC
    if (proximoNiver.getTime() < hojeUTC.getTime()) {
        proximoNiver.setFullYear(hoje.getFullYear() + 1);
    }

    return proximoNiver.getTime();
  };

  // Cria uma cópia e ordena
  const aniversariantesOrdenados = [...(stats.aniversariantes || [])].sort((a, b) => {
    return getProximoAniversario(a.data) - getProximoAniversario(b.data);
  });

  return (
    <div>
      <div className="container mx-auto px-6 py-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2 pt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Visão Geral</h1>
            <p className="text-gray-600">Métricas, Aniversariantes e Próximos voos</p>
          </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Período</Label> 
              <DateRangePicker
                startDate={filters.date.from}
                endDate={filters.date.to}
                onChange={({ startDate, endDate }) => setFilters({ ...filters, date: { from: startDate, to: endDate } })}
                placeholder="Selecione o período"
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={loadData} className="w-full" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                Filtrar
              </Button>
            </div>
          </div>
        </div>

        {/* KPIs Principais */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard title="Receita Bruta" value={`R$ ${faturamento_bruto_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} icon={<DollarSign className="w-5 h-5"/>} />
          <StatCard title="Deduções" value={`R$ ${repasses_fornecedores.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} icon={<FileText className="w-5 h-5"/>} />
          <StatCard title="Receita Líquida" value={`R$ ${receita_liquida_agencia_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} icon={<TrendingUp className="w-5 h-5"/>} />
          <StatCard title="Margem de Lucro" value={`${margem_lucro.toFixed(2)}%`} icon={<Percent className="w-5 h-5"/>} />
          <StatCard title="Vendas" value={volume_vendas} icon={<ShoppingBag className="w-5 h-5"/>} />
          <StatCard title="Ticket Médio" value={`R$ ${ticket_medio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} icon={<DollarSign className="w-5 h-5"/>} />
        </div>

        {/* Alertas */}
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Aniversariantes */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center"><Gift className="w-5 h-5 mr-2 text-pink-500"/> Aniversariantes (Próx. 7 dias)</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                    {aniversariantesOrdenados.length === 0 ? (
                        <p className="text-gray-500 text-sm text-center py-4">Nenhum aniversariante.</p>
                    ) : (
                        // AQUI USAMOS A LISTA ORDENADA
                        aniversariantesOrdenados.map((aniv, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <span className="font-medium text-gray-900">{aniv.nome}</span>
                                <div className="text-right">
                                    <span className="text-sm text-gray-600 block">{formatAniversario(aniv.data)}</span>
                                    {/* Opcional: Mostrar dia da semana */}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
            
            {/* Próximos Check-ins */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center"><PlaneTakeoff className="w-5 h-5 mr-2 text-cyan-500"/> Próximas Viagens (3 dias)</h3>
                 <div className="space-y-2 max-h-80 overflow-y-auto">
                    {(stats.checkins_proximos || []).length === 0 ? <p className="text-gray-500 text-sm text-center py-4">Nenhuma viagem próxima.</p> :
                    (stats.checkins_proximos || []).map((checkin, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <p className="font-medium text-gray-900">{checkin.clientes}</p>
                                <p className="text-sm text-gray-600">{checkin.origem} → {checkin.destino}</p>
                                <p className="text-sm font-semibold text-cyan-600 mt-1">
                                  {(() => {
                                    // Força a interpretação como UTC para não subtrair -3h e mudar o dia
                                    const dataPartida = new Date(checkin.data_partida);
                                    return dataPartida.toLocaleDateString('pt-BR', { 
                                        day: '2-digit', month: '2-digit', 
                                        hour: '2-digit', minute: '2-digit', 
                                        timeZone: 'UTC' // Importante: Força UTC na exibição
                                    });
                                  })()}
                                </p>
                                
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => handleShowDetails(checkin.venda_id)}><Eye className="w-4 h-4" /></Button>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Listas Rápidas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Top Clientes</h3>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                    {(stats.top_clientes || []).map((cliente, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-900">#{idx + 1} {cliente.nome}</span>
                            <span className="font-semibold text-green-600">R$ {(cliente.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Top Rotas</h3>
                 <div className="space-y-3 max-h-80 overflow-y-auto">
                    {(stats.top_rotas || []).map((rota, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-900">#{idx + 1} {rota.rota}</span>
                            <span className="font-semibold text-blue-600">{rota.quantidade}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
      </div>

      <DetalhesVendaModal
        venda={selectedVenda}
        isOpen={isModalOpen}
        isLoading={isModalLoading} 
        onClose={() => { setIsModalOpen(false); setSelectedVenda(null); }}
      />
    </div>
  );
}